import { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Book, Flag, Users, User, Home, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

const Services = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to log out. Please try again.",
      });
    } else {
      navigate("/auth");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4">
        {/* Navigation Header */}
        <div className="py-4 flex justify-between items-center">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="h-5 w-5" />
            <span>Back Home</span>
          </Link>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>

        <h1 className="text-4xl font-bold text-primary mb-8">Our Services</h1>
        
        <div className="space-y-12">
          {/* Coaches Section */}
          <section id="coaches" className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <User className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold text-primary">For Coaches</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Dedicated tools and resources for football coaches to create, manage, and share training drills. 
              Access a comprehensive library of exercises, customize training sessions, and track player progress 
              with our intuitive platform designed specifically for coaching professionals.
            </p>
          </section>

          {/* Trainers Section */}
          <section id="trainers" className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Users className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold text-primary">For Trainers</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Specialized features for personal trainers and fitness professionals working in football. 
              Create personalized training programs, monitor athlete development, and maintain detailed 
              progress reports all in one place.
            </p>
          </section>

          {/* Football Federations Section */}
          <section id="federations" className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Flag className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold text-primary">For Football Federations</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Comprehensive solutions for national football federations to standardize training methodologies, 
              implement development programs, and manage coach education initiatives. Our platform helps 
              federations maintain consistent training standards across all levels.
            </p>
          </section>

          {/* Football Associations Section */}
          <section id="associations" className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Flag className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold text-primary">For Football Associations</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Tools and resources designed for regional and local football associations to coordinate 
              training programs, organize competitions, and support member clubs. Streamline administrative 
              tasks and enhance communication between association members.
            </p>
          </section>

          {/* Drill Content Creators Section */}
          <section id="creators" className="bg-white rounded-lg p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Book className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold text-primary">For Drill Content Creators</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              A powerful platform for content creators to design, publish, and share innovative football 
              drills. Create engaging visual content, develop training sequences, and reach a wider 
              audience of coaching professionals through our marketplace.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Services;